/**
 * Pure currency helpers — no React, no side-effects.
 *
 * Extracted from CurrencyContext so the provider stays lean and these
 * helpers can be unit-tested + reused by non-React code paths (server-
 * side render, workers, tests).
 */

export const BASE_CURRENCY = "USD";

// ISO 3166-1 alpha-2 → ISO 4217. Only countries the app cares about.
export const COUNTRY_TO_CURRENCY = {
  US: "USD", CA: "CAD", GB: "GBP", AU: "AUD", NZ: "NZD", JP: "JPY", CN: "CNY",
  IN: "INR", BR: "BRL", MX: "MXN", ZA: "ZAR", SG: "SGD", HK: "HKD", KR: "KRW",
  CH: "CHF", SE: "SEK", NO: "NOK", DK: "DKK", PL: "PLN", CZ: "CZK", HU: "HUF",
  AE: "AED", SA: "SAR", IL: "ILS", TR: "TRY", TH: "THB", MY: "MYR", ID: "IDR",
  VN: "VND", PH: "PHP", NG: "NGN", EG: "EGP", KE: "KES",
  // Eurozone
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR", AT: "EUR",
  IE: "EUR", PT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR", EE: "EUR", SI: "EUR",
  SK: "EUR", LT: "EUR", LV: "EUR", MT: "EUR", CY: "EUR", HR: "EUR",
};

// Currencies whose minor units are usually 0 (¥, ₩, ₹ millions, etc.).
const ZERO_DECIMAL_CCY = new Set(["JPY", "KRW", "IDR", "VND"]);

// Currencies that all share the "$" narrow-symbol — must be labeled with a
// disambiguating prefix so a CAD user never mistakes them for USD.
const AMBIGUOUS_DOLLAR_CCY = new Set([
  "CAD", "AUD", "NZD", "SGD", "HKD", "MXN", "ARS", "CLP", "COP", "BRL", "TWD",
]);

const DISAMBIGUATED_PREFIX = {
  CAD: "CA$", AUD: "A$", NZD: "NZ$", SGD: "S$", HKD: "HK$",
  MXN: "MX$", ARS: "AR$", CLP: "CL$", COP: "CO$", BRL: "R$", TWD: "NT$",
};

export function localeFromBrowser() {
  if (typeof navigator === "undefined") return null;
  let raw = navigator.language || (navigator.languages && navigator.languages[0]) || null;
  if (!raw) return null;
  raw = String(raw).split("@")[0].trim();
  if (!/^[a-zA-Z]{2,3}(-[A-Za-z0-9]{2,8})*$/.test(raw)) return null;
  return raw;
}

export function countryFromBrowser() {
  const l = localeFromBrowser();
  if (!l) return null;
  const parts = l.split("-");
  return (parts[1] || "").toUpperCase() || null;
}

/**
 * Convert `amount` from `from` currency into `to` using USD-based rates.
 * Rates must map ISO code → amount of that currency you get for 1 USD.
 * Returns null when input is not a finite number; returns the raw amount
 * when rates aren't loaded yet or a rate is missing (safe degrade).
 */
export function convertAmount(amount, from, to, rates) {
  if (amount === null || amount === undefined || amount === "") return null;
  const n = Number(amount);
  if (!isFinite(n)) return null;
  if (from === to) return n;
  if (!rates || Object.keys(rates).length === 0) return n;
  if (from === "USD") return rates[to] ? n * rates[to] : n;
  if (to === "USD") return rates[from] ? n / rates[from] : n;
  if (rates[from] && rates[to]) return (n / rates[from]) * rates[to];
  return n;
}

/**
 * Format `amount` as a currency string.
 * opts: { currency, locale, compact, decimals, display }
 * Falls back progressively: opts.locale → browser locale → en-US → plain string.
 *
 * Currencies that share the "$" symbol (CAD, AUD, MXN, HKD, etc.) render with
 * a disambiguating prefix (CA$, A$, MX$, HK$) unless the caller passes
 * `display: "symbol"` explicitly. This prevents a Canadian user from
 * mistaking their CAD balances for USD.
 */
export function formatCurrency(amount, currency, opts = {}) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = Number(amount);
  if (!isFinite(n)) return "—";
  const code = (opts.currency || currency || BASE_CURRENCY).toUpperCase();
  const compact = !!opts.compact;
  const maxFrac = opts.decimals !== undefined
    ? opts.decimals
    : (compact ? 1 : (ZERO_DECIMAL_CCY.has(code) ? 0 : 2));
  const minFrac = opts.decimals !== undefined ? opts.decimals : 0;

  // For ambiguous "$" currencies (CAD/AUD/etc.), prepend the disambiguating
  // ISO-prefix and format the number itself as a plain (non-currency) number.
  if (AMBIGUOUS_DOLLAR_CCY.has(code) && !opts.display) {
    const preferred = opts.locale || localeFromBrowser() || "en-US";
    try {
      const numberOptions = {
        maximumFractionDigits: maxFrac,
        minimumFractionDigits: minFrac,
        notation: compact ? "compact" : "standard",
      };
      const formatted = new Intl.NumberFormat(preferred, numberOptions).format(Math.abs(n));
      const sign = n < 0 ? "-" : "";
      const prefix = DISAMBIGUATED_PREFIX[code] || `${code} `;
      return `${sign}${prefix}${formatted}`;
    } catch (_) { /* fall through to standard path */ }
  }

  const options = {
    style: "currency",
    currency: code,
    maximumFractionDigits: maxFrac,
    minimumFractionDigits: minFrac,
    notation: compact ? "compact" : "standard",
    currencyDisplay: opts.display || "narrowSymbol",
  };
  const preferred = opts.locale || localeFromBrowser() || "en-US";
  const candidates = preferred === "en-US" ? [preferred] : [preferred, "en-US"];
  for (const loc of candidates) {
    try {
      return new Intl.NumberFormat(loc, options).format(n);
    } catch (_) { /* try next */ }
  }
  return `${code} ${n.toLocaleString()}`;
}
